import React, { useEffect, useState } from "react";
import { getWarehouses, createWarehouse, updateWarehouse, deleteWarehouse } from "../services/procurementApi";
import {
  getCategoryConfigs,
  updateCategoryConfig,
  getScanCategories,
  getCategoryBucketMap,
  setCategoryBucketMap,
  getLocations,
  getGridSummary,
  bulkUpdateGrid,
  bulkDeleteGrid,
  bulkGenerateLocations,
} from "../services/warehouseApi";
import "./ProcurementPage.css";

const WarehouseLocationsPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [newWarehouse, setNewWarehouse] = useState({ code: "", name: "", address: "" });
  const [editingWh, setEditingWh] = useState(null);
  const [categoryConfigs, setCategoryConfigs] = useState([]);
  const [realCategories, setRealCategories] = useState([]);
  const [bucketMap, setBucketMap] = useState({});
  const [locations, setLocations] = useState([]);
  const [gridRows, setGridRows] = useState([]); // summary per row_code
  const [editingGrid, setEditingGrid] = useState(null); // row being edited
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const [grid, setGrid] = useState({
    row_code: "A", shelf_count: 4, rack_count: 3, bin_count: 1,
    allowed_category: "shoes", zone_type: "stock", is_locked: false, max_capacity: 1,
  });

  const loadAll = async (wh) => {
    try {
      const [whs, cats, realCats, mappings] = await Promise.all([
        getWarehouses(), getCategoryConfigs(), getScanCategories(), getCategoryBucketMap(),
      ]);
      setWarehouses(whs);
      setCategoryConfigs(cats);
      setRealCategories(realCats);
      setBucketMap(Object.fromEntries(mappings.map((m) => [m.category_name, m.bucket_key])));
      const targetWh = wh || whs[0]?.id || "";
      setWarehouseId(targetWh);
      if (targetWh) {
        const [locs, grids] = await Promise.all([
          getLocations({ warehouse_id: targetWh }),
          getGridSummary(targetWh),
        ]);
        setLocations(locs);
        setGridRows(grids);
      }
    } catch (err) {
      setMsg({ type: "error", text: "Failed to load warehouse setup data." });
    }
  };

  useEffect(() => { loadAll(); }, []);

  const refreshLocations = async (wh) => {
    const [locs, grids] = await Promise.all([
      getLocations({ warehouse_id: wh }),
      getGridSummary(wh),
    ]);
    setLocations(locs);
    setGridRows(grids);
  };

  // ---- Warehouse CRUD ----
  const handleCreateWarehouse = async (e) => {
    e.preventDefault();
    if (!newWarehouse.code || !newWarehouse.name) return;
    try {
      await createWarehouse(newWarehouse);
      setNewWarehouse({ code: "", name: "", address: "" });
      setMsg({ type: "success", text: "Warehouse created." });
      loadAll();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to create warehouse." });
    }
  };
  const handleUpdateWarehouse = async (e) => {
    e.preventDefault();
    if (!editingWh) return;
    try {
      await updateWarehouse(editingWh.id, { code: editingWh.code, name: editingWh.name, address: editingWh.address });
      setEditingWh(null);
      setMsg({ type: "success", text: "Warehouse updated." });
      loadAll();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to update warehouse." });
    }
  };
  const handleDeleteWarehouse = async (wh) => {
    if (!window.confirm(`Delete warehouse "${wh.code} — ${wh.name}"? This cannot be undone.`)) return;
    try {
      await deleteWarehouse(wh.id);
      setMsg({ type: "success", text: "Warehouse deleted." });
      loadAll();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to delete warehouse." });
    }
  };

  // ---- Category / Capacity ----
  const handleCapacityChange = async (cfg, value) => {
    const updated = await updateCategoryConfig(cfg.id, { max_capacity: Number(value) });
    setCategoryConfigs((prev) => prev.map((c) => (c.id === cfg.id ? updated : c)));
  };
  const handleBucketAssign = async (categoryName, bucketKey) => {
    await setCategoryBucketMap(categoryName, bucketKey);
    setBucketMap((prev) => ({ ...prev, [categoryName]: bucketKey }));
    setMsg({ type: "success", text: "Category mapped." });
  };

  // ---- Bulk Generate ----
  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    if (!warehouseId) { setMsg({ type: "error", text: "Select a warehouse first." }); return; }
    setLoading(true);
    try {
      const result = await bulkGenerateLocations({ ...grid, warehouse_id: warehouseId });
      setMsg({ type: "success", text: `Generated ${result.generated} location(s).` });
      refreshLocations(warehouseId);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to generate locations." });
    } finally { setLoading(false); }
  };

  // ---- Grid Update / Delete ----
  const handleGridUpdate = async () => {
    if (!editingGrid) return;
    setLoading(true);
    try {
      const result = await bulkUpdateGrid({ ...editingGrid, warehouse_id: warehouseId });
      setMsg({ type: "success", text: result.message });
      setEditingGrid(null);
      refreshLocations(warehouseId);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to update grid." });
    } finally { setLoading(false); }
  };

  const handleGridDelete = async (row) => {
    if (!window.confirm(`Delete entire Row ${row.row_code}? All empty locations in this row will be removed.`)) return;
    try {
      const result = await bulkDeleteGrid({ warehouse_id: warehouseId, row_code: row.row_code });
      setMsg({ type: "success", text: result.message });
      refreshLocations(warehouseId);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to delete grid row." });
    }
  };

  // ---- Visual grid helpers ----
  const buildGridData = () => {
    const rows = {};
    locations.forEach((loc) => {
      const r = loc.row_code || "?";
      const s = loc.shelf_num || "?";
      const rk = loc.rack_num || "?";
      if (!rows[r]) rows[r] = {};
      if (!rows[r][s]) rows[r][s] = {};
      if (!rows[r][s][rk]) rows[r][s][rk] = [];
      rows[r][s][rk].push(loc);
    });
    return rows;
  };
  const occColor = (loc) => {
    if (loc.current_occupancy >= loc.max_capacity) return "#fecaca";
    if (loc.current_occupancy > 0) return "#fef3c7";
    return "#dcfce7";
  };

  return (
    <div className="procurement-page">
      <h1>Warehouse Setup</h1>
      <p className="subtitle">
        Create a warehouse, map categories to storage buckets, set capacity rules, then generate physical
        locations (Row → Shelf → Rack → Bin).
      </p>

      {msg && <div className={`proc-msg ${msg.type}`}>{msg.text}</div>}

      {/* ---- STEP 1 ---- */}
      <div className="proc-section">
        <h2><span className="proc-step-badge">1</span> Manage Warehouses</h2>
        <form onSubmit={handleCreateWarehouse} className="proc-grid">
          <div className="proc-field"><label>Code</label><input value={newWarehouse.code} onChange={(e) => setNewWarehouse({ ...newWarehouse, code: e.target.value })} placeholder="WH-MAIN" /></div>
          <div className="proc-field"><label>Name</label><input value={newWarehouse.name} onChange={(e) => setNewWarehouse({ ...newWarehouse, name: e.target.value })} placeholder="Main Warehouse" /></div>
          <div className="proc-field"><label>Address</label><input value={newWarehouse.address} onChange={(e) => setNewWarehouse({ ...newWarehouse, address: e.target.value })} placeholder="Optional" /></div>
          <div className="proc-field" style={{ alignSelf: "end" }}><button type="submit" className="proc-btn primary">+ Add</button></div>
        </form>
        {warehouses.length > 0 && (
          <table className="proc-table" style={{ marginTop: 12 }}>
            <thead><tr><th>Code</th><th>Name</th><th>Address</th><th style={{ width: 140 }}>Actions</th></tr></thead>
            <tbody>
              {warehouses.map((w) =>
                editingWh && editingWh.id === w.id ? (
                  <tr key={w.id}>
                    <td><input value={editingWh.code} onChange={(e) => setEditingWh({ ...editingWh, code: e.target.value })} style={{ width: "100%" }} /></td>
                    <td><input value={editingWh.name} onChange={(e) => setEditingWh({ ...editingWh, name: e.target.value })} style={{ width: "100%" }} /></td>
                    <td><input value={editingWh.address || ""} onChange={(e) => setEditingWh({ ...editingWh, address: e.target.value })} style={{ width: "100%" }} /></td>
                    <td>
                      <button className="proc-btn primary" style={{ padding: "5px 10px", fontSize: 12, marginRight: 4 }} onClick={handleUpdateWarehouse}>Save</button>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12 }} onClick={() => setEditingWh(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={w.id}>
                    <td className="proc-unit-cost">{w.code}</td>
                    <td>{w.name}</td>
                    <td style={{ color: "#64748b", fontSize: 12 }}>{w.address || "—"}</td>
                    <td>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12, marginRight: 4 }} onClick={() => setEditingWh({ id: w.id, code: w.code, name: w.name, address: w.address || "" })}>✏️</button>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12, color: "#ef4444" }} onClick={() => handleDeleteWarehouse(w)}>🗑</button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- STEP 2 ---- */}
      <div className="proc-section">
        <h2><span className="proc-step-badge">2</span> Map categories to storage buckets</h2>
        <table className="proc-table">
          <thead><tr><th>Smart Scan category</th><th>Items</th><th>Storage bucket</th></tr></thead>
          <tbody>
            {realCategories.map((c) => (
              <tr key={c.l3_category}>
                <td>{c.l3_category}</td><td>{c.item_count}</td>
                <td>
                  <select value={bucketMap[c.l3_category] || ""} onChange={(e) => handleBucketAssign(c.l3_category, e.target.value)}>
                    <option value="">— Not mapped —</option>
                    {categoryConfigs.map((cfg) => <option key={cfg.category_key} value={cfg.category_key}>{cfg.category_name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {realCategories.length === 0 && <tr><td colSpan={3} style={{ color: "#9ca3af" }}>No Smart Scan categories found yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ---- STEP 3 ---- */}
      <div className="proc-section">
        <h2><span className="proc-step-badge">3</span> Storage Bucket Capacity Rules</h2>
        <table className="proc-table">
          <thead><tr><th>Bucket</th><th>Storage type</th><th>Max capacity</th><th>Locked?</th></tr></thead>
          <tbody>
            {categoryConfigs.map((cfg) => (
              <tr key={cfg.id}>
                <td>{cfg.category_name}</td><td>{cfg.storage_type}</td>
                <td><input type="number" style={{ width: 70, padding: 4 }} defaultValue={cfg.max_capacity} onBlur={(e) => handleCapacityChange(cfg, e.target.value)} /></td>
                <td>{cfg.requires_locked_storage ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- STEP 4 ---- */}
      <div className="proc-section">
        <h2><span className="proc-step-badge">4</span> Generate Physical Locations</h2>
        <form onSubmit={handleBulkGenerate}>
          <div className="proc-grid" style={{ marginBottom: 14 }}>
            <div className="proc-field">
              <label>Warehouse</label>
              <select value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); refreshLocations(e.target.value); }}>
                <option value="">Select warehouse</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
              </select>
            </div>
            <div className="proc-field"><label>Row code</label><input value={grid.row_code} onChange={(e) => setGrid({ ...grid, row_code: e.target.value })} /></div>
            <div className="proc-field">
              <label>Storage bucket</label>
              <select value={grid.allowed_category} onChange={(e) => {
                const cfg = categoryConfigs.find((c) => c.category_key === e.target.value);
                setGrid({ ...grid, allowed_category: e.target.value, is_locked: !!cfg?.requires_locked_storage, max_capacity: cfg?.max_capacity || 10 });
              }}>
                {categoryConfigs.map((c) => <option key={c.category_key} value={c.category_key}>{c.category_name}</option>)}
              </select>
            </div>
            <div className="proc-field"><label>Shelves</label><input type="number" value={grid.shelf_count} onChange={(e) => setGrid({ ...grid, shelf_count: Number(e.target.value) })} /></div>
            <div className="proc-field"><label>Racks / shelf</label><input type="number" value={grid.rack_count} onChange={(e) => setGrid({ ...grid, rack_count: Number(e.target.value) })} /></div>
            <div className="proc-field"><label>Bins / rack</label><input type="number" value={grid.bin_count} onChange={(e) => setGrid({ ...grid, bin_count: Number(e.target.value) })} /></div>
            <div className="proc-field"><label>Max capacity</label><input type="number" value={grid.max_capacity} onChange={(e) => setGrid({ ...grid, max_capacity: Number(e.target.value) })} /></div>
          </div>
          <button type="submit" className="proc-btn primary" disabled={loading}>{loading ? "Generating…" : "Generate Location Grid"}</button>
        </form>
      </div>

      {/* ---- STEP 5: Grid Summary (Update / Delete) ---- */}
      {gridRows.length > 0 && (
        <div className="proc-section">
          <h2><span className="proc-step-badge">5</span> Manage Generated Grids</h2>
          <p className="subtitle" style={{ marginBottom: 10 }}>
            Each row below is one generated grid (by Row code). Edit to change shelf/rack/bin counts — excess empty locations get deleted, new ones get added. Occupied locations are always kept safe.
          </p>
          <table className="proc-table">
            <thead>
              <tr><th>Row</th><th>Shelves</th><th>Racks</th><th>Bins</th><th>Category</th><th>Capacity</th><th>Locations</th><th>Used</th><th style={{ width: 130 }}>Actions</th></tr>
            </thead>
            <tbody>
              {gridRows.map((g) =>
                editingGrid && editingGrid.row_code === g.row_code ? (
                  <tr key={g.row_code}>
                    <td className="proc-unit-cost">{g.row_code}</td>
                    <td><input type="number" value={editingGrid.shelf_count} onChange={(e) => setEditingGrid({ ...editingGrid, shelf_count: Number(e.target.value) })} style={{ width: 55 }} /></td>
                    <td><input type="number" value={editingGrid.rack_count} onChange={(e) => setEditingGrid({ ...editingGrid, rack_count: Number(e.target.value) })} style={{ width: 55 }} /></td>
                    <td><input type="number" value={editingGrid.bin_count} onChange={(e) => setEditingGrid({ ...editingGrid, bin_count: Number(e.target.value) })} style={{ width: 55 }} /></td>
                    <td>
                      <select value={editingGrid.allowed_category || ""} onChange={(e) => setEditingGrid({ ...editingGrid, allowed_category: e.target.value })} style={{ fontSize: 12 }}>
                        {categoryConfigs.map((c) => <option key={c.category_key} value={c.category_key}>{c.category_name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" value={editingGrid.max_capacity} onChange={(e) => setEditingGrid({ ...editingGrid, max_capacity: Number(e.target.value) })} style={{ width: 55 }} /></td>
                    <td>{g.total_locations}</td>
                    <td>{Number(g.total_occupancy)}</td>
                    <td>
                      <button className="proc-btn primary" style={{ padding: "5px 10px", fontSize: 12, marginRight: 4 }} onClick={handleGridUpdate} disabled={loading}>{loading ? "…" : "Save"}</button>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12 }} onClick={() => setEditingGrid(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={g.row_code}>
                    <td className="proc-unit-cost">{g.row_code}</td>
                    <td>{g.shelf_count}</td>
                    <td>{g.rack_count}</td>
                    <td>{g.bin_count}</td>
                    <td>{g.allowed_category || "—"}</td>
                    <td>{g.max_capacity}</td>
                    <td>{g.total_locations}</td>
                    <td>
                      <span className={`status-chip ${Number(g.total_occupancy) > 0 ? "status-picked" : "status-stored"}`}>
                        {Number(g.total_occupancy)}
                      </span>
                    </td>
                    <td>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12, marginRight: 4 }} onClick={() => setEditingGrid({
                        row_code: g.row_code, shelf_count: g.shelf_count, rack_count: g.rack_count, bin_count: g.bin_count,
                        allowed_category: g.allowed_category, zone_type: g.zone_type, is_locked: !!g.is_locked, max_capacity: g.max_capacity,
                      })}>✏️</button>
                      <button className="proc-btn secondary" style={{ padding: "5px 10px", fontSize: 12, color: "#ef4444" }} onClick={() => handleGridDelete(g)}>🗑</button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Locations Table + Visual Grid ---- */}
      <div className="proc-section">
        <h2 style={{ justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            Locations <span className="proc-count-pill">{locations.length}</span>
          </span>
          {locations.length > 0 && (
            <button className="proc-btn secondary" style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => setShowGrid(!showGrid)}>
              {showGrid ? "📋 Table View" : "🗺️ Visual Grid"}
            </button>
          )}
        </h2>

        {!showGrid ? (
          <table className="proc-table">
            <thead><tr><th>Code</th><th>Category</th><th>Occupancy</th><th>Zone</th></tr></thead>
            <tbody>
              {locations.slice(0, 50).map((l) => {
                const full = l.current_occupancy >= l.max_capacity;
                return (
                  <tr key={l.id}>
                    <td className="proc-unit-cost">{l.full_code}</td>
                    <td>{l.allowed_category || "—"}</td>
                    <td><span className={`status-chip ${full ? "status-picked" : "status-stored"}`}>{l.current_occupancy} / {l.max_capacity}</span></td>
                    <td>{l.zone_type}</td>
                  </tr>
                );
              })}
              {locations.length === 0 && <tr><td colSpan={4} style={{ color: "#9ca3af" }}>No locations yet.</td></tr>}
            </tbody>
          </table>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {Object.entries(buildGridData()).sort().map(([rowCode, shelves]) => (
              <div key={rowCode} style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Row {rowCode}</h3>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "6px 10px", background: "#f1f5f9", fontWeight: 700, color: "#64748b", textAlign: "left" }}>Shelf</th>
                      {(() => {
                        const maxRacks = Math.max(...Object.values(shelves).map((sh) => Object.keys(sh).length));
                        return Array.from({ length: maxRacks }, (_, i) => (
                          <th key={i} style={{ padding: "6px 10px", background: "#f1f5f9", fontWeight: 700, color: "#64748b", textAlign: "center" }}>Rack {String(i + 1).padStart(2, "0")}</th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(shelves).sort().map(([shelfNum, racks]) => (
                      <tr key={shelfNum}>
                        <td style={{ padding: "6px 10px", fontWeight: 700, borderBottom: "1px solid #f1f5f9", color: "#334155" }}>S{shelfNum}</td>
                        {Object.entries(racks).sort().map(([rackNum, bins]) => (
                          <td key={rackNum} style={{ padding: "4px 6px", borderBottom: "1px solid #f1f5f9", textAlign: "center" }}>
                            <div style={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
                              {bins.map((bin) => (
                                <div key={bin.id} title={`${bin.full_code}\n${bin.allowed_category || "any"}\n${bin.current_occupancy}/${bin.max_capacity}`}
                                  style={{
                                    minWidth: 44, height: 44, borderRadius: 6, background: occColor(bin),
                                    border: `1.5px solid ${bin.current_occupancy >= bin.max_capacity ? "#f87171" : bin.current_occupancy > 0 ? "#fbbf24" : "#86efac"}`,
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                    fontSize: 9, fontWeight: 700, color: "#374151", cursor: "default", padding: "2px 4px",
                                  }}>
                                  <span style={{ fontSize: 8, color: "#64748b", fontWeight: 600 }}>B{bin.layer_bin_num}</span>
                                  <span>{bin.current_occupancy}/{bin.max_capacity}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 11, color: "#64748b" }}>
                  <span><span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: "#dcfce7", border: "1px solid #86efac", verticalAlign: "middle", marginRight: 4 }}></span>Empty</span>
                  <span><span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: "#fef3c7", border: "1px solid #fbbf24", verticalAlign: "middle", marginRight: 4 }}></span>Partial</span>
                  <span><span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: "#fecaca", border: "1px solid #f87171", verticalAlign: "middle", marginRight: 4 }}></span>Full</span>
                </div>
              </div>
            ))}
            {locations.length === 0 && <p style={{ color: "#9ca3af", textAlign: "center", padding: 24 }}>No locations yet.</p>}
          </div>
        )}
        {!showGrid && locations.length > 50 && <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Showing first 50 of {locations.length}.</p>}
      </div>
    </div>
  );
};

export default WarehouseLocationsPage;
